import { z } from 'zod';
import { SourceSystem, StockEntryType } from '../enums.js';
import { DecimalString, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

/**
 * Stock Ledger Entry — append-only core движение склада.
 * Один entry = одна строка движения (item × warehouse × batch).
 *
 * Инварианты:
 * - balance_qty_after / balance_value_after пересчитываются при append.
 * - Отмена — только через compensating entry с cancelled_by_voucher ссылкой.
 * - До onboarding date допустим только voucher_type='opening_stock'.
 */
export const StockLedgerEntry = z.object({
  id: UuidString,
  entity_id: UuidString,

  item_id: UuidString,
  warehouse_id: UuidString,
  batch_id: UuidString.nullable(),
  serial_no: z.string().nullable(),

  posting_datetime: IsoDateTime,

  qty_change: DecimalString, // +приход / −расход
  balance_qty_after: DecimalString,

  valuation_rate: MoneyAmount,
  amount: MoneyAmount,
  balance_value_after: MoneyAmount,

  /** Опциональный "сырой" тип Stock Entry для traceability (null для opening_stock / reconciliation). */
  stock_entry_type: StockEntryType.nullable(),

  voucher_type: z.enum([
    'stock_entry',
    'stock_reconciliation',
    'opening_stock',
    'landed_cost',
    'work_order',
    'compensating',
  ]),
  voucher_id: UuidString,
  voucher_line_id: UuidString.nullable(),

  is_cancelled: z.boolean().default(false),
  cancelled_by_voucher: UuidString.nullable(),

  source_system: SourceSystem.default('staiter'),

  /**
   * S-FIX-5-BE.2.1 + 8.2 — voucher_link_target для FE drill-down на originating документ.
   * Centralized mapping (BE решает, какой URL открывать); FE рендерит как `<Link to={target}/${voucher_id}>`.
   * null — для opening_stock и compensating entries (нет originating UI route).
   * Optional на уровне Zod — для backward-compat со старыми SLE-snapshots в БД.
   */
  voucher_link_target: z.string().nullable().optional(),

  created_at: IsoDateTime,
});
export type StockLedgerEntry = z.infer<typeof StockLedgerEntry>;

/**
 * S-FIX-5-BE.2.1 — voucher_type → route mapping для FE drill-down.
 *
 * `null` — нет drill-down (opening_stock = историческая точка, compensating = reversal pair).
 */
export const VOUCHER_LINK_TARGET_MAP: Record<
  z.infer<typeof StockLedgerEntry>['voucher_type'],
  string | null
> = {
  stock_entry: '/stock-entries',
  stock_reconciliation: '/stock-reconciliation',
  opening_stock: null,
  landed_cost: '/landed-cost',
  work_order: '/work-orders',
  compensating: null,
};

export function getVoucherLinkTarget(
  voucherType: z.infer<typeof StockLedgerEntry>['voucher_type'],
): string | null {
  return VOUCHER_LINK_TARGET_MAP[voucherType];
}
